// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

import { Workbox } from 'workbox-window';
import { versionManager } from './utils/versionManager';

let updateAvailable = false;
let registrationInstance: ServiceWorkerRegistration | null = null;
let updateCallbacks: Array<(updateInfo: any) => void> = [];

export function register() {
  if ('serviceWorker' in navigator) {
    // Use native service worker registration for development
    const swUrl = '/service-worker.js';
    
    navigator.serviceWorker.register(swUrl)
      .then((registration) => {
        console.log('Service Worker registered successfully:', registration);
        registrationInstance = registration;
        
        // Handle updates (disabled - no notifications)
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                updateAvailable = true;
                // Silent update - no notification shown
                console.log('New version available, will update on next reload');
              }
            });
          }
        });
        
        // Check for updates periodically (disabled)
        // setInterval(() => {
        //   registration.update();
        // }, 5 * 60 * 1000); // Check every 5 minutes
        
        return registration;
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
        
        // Fallback: try to register the old sw.js file
        return navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('Fallback Service Worker registered:', registration);
            registrationInstance = registration;
            return registration;
          })
          .catch((fallbackError) => {
            console.log('Fallback Service Worker registration also failed:', fallbackError);
          });
      });

    // Handle controller change (page refresh after update)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (updateAvailable) {
        showToast('App updated to latest version!');
        updateAvailable = false;
      }
    });
  }
}

// Add callback for update notifications
export function onUpdateAvailable(callback: (updateInfo: any) => void): () => void {
  updateCallbacks.push(callback);
  return () => {
    updateCallbacks = updateCallbacks.filter(cb => cb !== callback);
  };
}

function showToast(message: string) {
  // You can replace this with your preferred toast notification
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.position = 'fixed';
  toast.style.bottom = '20px';
  toast.style.left = '50%';
  toast.style.transform = 'translateX(-50%)';
  toast.style.backgroundColor = '#333';
  toast.style.color = '#fff';
  toast.style.padding = '10px 20px';
  toast.style.borderRadius = '4px';
  toast.style.zIndex = '9999';
  document.body.appendChild(toast);
  
  setTimeout(() => {
    document.body.removeChild(toast);
  }, 5000);
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}

// Get current version for display
export function getAppVersion(): string {
  const version = (window as any).APP_VERSION || '1.0.0';
  const build = (window as any).APP_BUILD || 0;
  return `${version} (Build ${build})`;
}

// Check if there's a pending update
export function hasPendingUpdate(): boolean {
  return !!localStorage.getItem('pendingUpdateVersion');
}

// Get pending update version
export function getPendingUpdateVersion(): string | null {
  return localStorage.getItem('pendingUpdateVersion');
}